import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { useAuth } from "@/core/auth/auth-provider";
import {
  subscribeToDomainLegacyOwnedRejections,
  type DomainLegacyOwnedRejection,
} from "./domain-legacy-owned";
import {
  HttpWriteAuthorityClient,
  type WriteAuthorityClient,
  type WriteAuthorityReadResult,
} from "./write-authority-client";
import {
  WRITE_AUTHORITY_DOMAINS,
  type DomainAuthority,
  type WriteAuthorityDomain,
} from "./write-authority-domains";

/**
 * Where the current per-domain answer came from:
 * - `initial`: nothing read yet (bootstrap, or reset after sign-out);
 * - `fresh`: the latest read was a validated `200` with `stale: false`;
 * - `stale`: the latest read was a `200` with `stale: true` (service SSM
 *   read failed, last cached document served) — every domain is `legacy`;
 * - `unavailable`: non-200, non-JSON, malformed/unknown-schema body, a
 *   network failure, a timed-out read, or a fresh snapshot past its max age
 *   — every domain is `legacy`.
 */
export type WriteAuthoritySource = "initial" | "fresh" | "stale" | "unavailable";

export interface WriteAuthoritySnapshot {
  source: WriteAuthoritySource;
  domains: Readonly<Record<WriteAuthorityDomain, DomainAuthority>>;
}

export interface DomainWriteAuthority {
  authority: DomainAuthority;
  source: WriteAuthoritySource;
}

/**
 * Matches the service's cache TTL ceiling (`WRITE_AUTHORITY_CACHE_TTL_SECONDS`
 * ≤ 30, the `Cache-Control: max-age` on fresh bodies), so the SPA never
 * trusts a document longer than the service itself does.
 */
export const WRITE_AUTHORITY_REFRESH_INTERVAL_MS = 30_000;

/**
 * R3: every read is bounded so a request that never settles cannot hold a
 * cached `new` open or wedge the poll loop; a timed-out read is `unavailable`.
 */
export const WRITE_AUTHORITY_READ_TIMEOUT_MS = 10_000;

/**
 * R3: a fresh snapshot is trusted for at most two refresh intervals after it
 * was received. If nothing has replaced it by then (throttled timers, a
 * stalled refresh), it expires to `unavailable` on its own.
 */
export const WRITE_AUTHORITY_SNAPSHOT_MAX_AGE_MS = 2 * WRITE_AUTHORITY_REFRESH_INTERVAL_MS;

const ALL_LEGACY: Readonly<Record<WriteAuthorityDomain, DomainAuthority>> = Object.freeze(
  Object.fromEntries(WRITE_AUTHORITY_DOMAINS.map((domain) => [domain, "legacy"])) as Record<
    WriteAuthorityDomain,
    DomainAuthority
  >,
);

export const FAIL_CLOSED_WRITE_AUTHORITY: WriteAuthoritySnapshot = Object.freeze({
  source: "initial",
  domains: ALL_LEGACY,
});

const EXPIRED_WRITE_AUTHORITY: WriteAuthoritySnapshot = Object.freeze({
  source: "unavailable",
  domains: ALL_LEGACY,
});

/**
 * Fail-closed evaluation (contract "Consumption rules"): a domain is `new`
 * ONLY from a fresh document that lists it as `new`; absence, a stale body
 * (even one that says `new`), and every unavailable outcome are `legacy`.
 */
export function evaluateWriteAuthority(result: WriteAuthorityReadResult): WriteAuthoritySnapshot {
  if (result.kind === "unavailable") {
    return { source: "unavailable", domains: ALL_LEGACY };
  }
  if (result.document.stale) {
    return { source: "stale", domains: ALL_LEGACY };
  }
  const domains = { ...ALL_LEGACY };
  for (const domain of WRITE_AUTHORITY_DOMAINS) {
    if (result.document.domains[domain]?.authority === "new") {
      domains[domain] = "new";
    }
  }
  return { source: "fresh", domains };
}

export interface WriteAuthorityContextValue {
  snapshot: WriteAuthoritySnapshot;
  /** Latest unhandled 409 `DOMAIN_LEGACY_OWNED` rejection for the shell host. */
  rejection: DomainLegacyOwnedRejection | null;
  reportLegacyOwnedRejection: (rejection: DomainLegacyOwnedRejection) => void;
  dismissLegacyOwnedRejection: () => void;
  /**
   * U1: a mounted surface that renders its own pointer for a domain's 409s
   * claims the domain; while claimed, reports for it never reach the shell
   * host (no duplicate, and no flash before the surface's own render).
   * Returns the release for unmount.
   */
  claimLegacyOwnedRejections: (domain: WriteAuthorityDomain) => () => void;
}

export type LegacyOwnedRejectionState = Omit<WriteAuthorityContextValue, "snapshot">;

/**
 * Rejection report/dismiss/claim state shared by the production provider and
 * the test fixture provider so hosts and surfaces behave identically under
 * both.
 */
export function useLegacyOwnedRejectionState(): LegacyOwnedRejectionState {
  const [rejection, setRejection] = useState<DomainLegacyOwnedRejection | null>(null);
  const claimsRef = useRef(new Map<WriteAuthorityDomain, number>());

  const reportLegacyOwnedRejection = useCallback((next: DomainLegacyOwnedRejection) => {
    if ((claimsRef.current.get(next.domain) ?? 0) > 0) {
      return;
    }
    setRejection(next);
  }, []);
  const dismissLegacyOwnedRejection = useCallback(() => setRejection(null), []);
  const claimLegacyOwnedRejections = useCallback((domain: WriteAuthorityDomain) => {
    const claims = claimsRef.current;
    claims.set(domain, (claims.get(domain) ?? 0) + 1);
    return () => {
      const remaining = (claims.get(domain) ?? 1) - 1;
      if (remaining <= 0) {
        claims.delete(domain);
      } else {
        claims.set(domain, remaining);
      }
    };
  }, []);

  return useMemo(
    () => ({
      rejection,
      reportLegacyOwnedRejection,
      dismissLegacyOwnedRejection,
      claimLegacyOwnedRejections,
    }),
    [
      claimLegacyOwnedRejections,
      dismissLegacyOwnedRejection,
      rejection,
      reportLegacyOwnedRejection,
    ],
  );
}

export const WriteAuthorityContext = createContext<WriteAuthorityContextValue | undefined>(
  undefined,
);

export interface WriteAuthorityProviderProps {
  children: ReactNode;
  client?: WriteAuthorityClient;
  refreshIntervalMs?: number;
}

/**
 * One bounded read: settles with the client's answer, or with `timeout`
 * once `timeoutMs` elapses, or as soon as the parent signal aborts (generation
 * change; the caller discards that answer). R4: every outcome goes through
 * the same idempotent `settle`, which clears the timer, drops the parent
 * listener, and aborts the request exactly once — repeated timeouts leave
 * no listeners behind and an unmount leaves no timer pending.
 */
function readWithTimeout(
  client: WriteAuthorityClient,
  parentSignal: AbortSignal,
  timeoutMs: number,
): Promise<WriteAuthorityReadResult> {
  return new Promise((resolve) => {
    const controller = new AbortController();
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const settle = (result: WriteAuthorityReadResult): void => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timer);
      parentSignal.removeEventListener("abort", onParentAbort);
      controller.abort();
      resolve(result);
    };
    const onParentAbort = (): void => settle({ kind: "unavailable", reason: "network" });
    if (parentSignal.aborted) {
      onParentAbort();
      return;
    }
    parentSignal.addEventListener("abort", onParentAbort);
    timer = setTimeout(() => settle({ kind: "unavailable", reason: "timeout" }), timeoutMs);
    try {
      client.read(controller.signal).then(settle, () => {
        settle({ kind: "unavailable", reason: "network" });
      });
    } catch {
      settle({ kind: "unavailable", reason: "network" });
    }
  });
}

/**
 * T034: reads `GET /write-authority` once an authenticated session exists,
 * refreshes on a recursive timer scheduled only after the previous read
 * settles (plus once when the document becomes visible again), keeps a
 * single request in flight, and drops late responses from a superseded
 * generation. Resets to fail-closed while unauthenticated or signing out —
 * the snapshot AND the shell host's latest 409 rejection, so a sign-out that
 * keeps the SPA mounted (no identity-provider redirect) never carries one
 * user's pointer into the next sign-in; transport reports are ignored while
 * no session is active for the same reason.
 * R3: each read is bounded by `WRITE_AUTHORITY_READ_TIMEOUT_MS` and a fresh
 * snapshot expires after `WRITE_AUTHORITY_SNAPSHOT_MAX_AGE_MS`.
 */
export function WriteAuthorityProvider({
  children,
  client,
  refreshIntervalMs = WRITE_AUTHORITY_REFRESH_INTERVAL_MS,
}: WriteAuthorityProviderProps): ReactElement {
  const auth = useAuth();
  const sessionActive = auth.session?.authenticated === true && auth.status !== "logout";
  const clientRef = useRef<WriteAuthorityClient>(client ?? new HttpWriteAuthorityClient());
  const [snapshot, setSnapshot] = useState<WriteAuthoritySnapshot>(FAIL_CLOSED_WRITE_AUTHORITY);
  const rejectionState = useLegacyOwnedRejectionState();
  const { dismissLegacyOwnedRejection, reportLegacyOwnedRejection } = rejectionState;
  const generationRef = useRef(0);

  useEffect(() => {
    const generation = ++generationRef.current;
    if (!sessionActive) {
      setSnapshot(FAIL_CLOSED_WRITE_AUTHORITY);
      dismissLegacyOwnedRejection();
      return;
    }

    const controller = new AbortController();
    let refreshTimer: ReturnType<typeof setTimeout> | undefined;
    let expiryTimer: ReturnType<typeof setTimeout> | undefined;
    let inFlight = false;
    const live = (): boolean => generationRef.current === generation;

    const read = async (): Promise<void> => {
      if (!live() || inFlight) {
        return;
      }
      inFlight = true;
      const result = await readWithTimeout(
        clientRef.current,
        controller.signal,
        WRITE_AUTHORITY_READ_TIMEOUT_MS,
      );
      inFlight = false;
      if (!live()) {
        return;
      }
      clearTimeout(expiryTimer);
      const next = evaluateWriteAuthority(result);
      setSnapshot(next);
      if (next.source === "fresh") {
        expiryTimer = setTimeout(() => {
          // U16: a throttled tab can wake this together with the refresh. With
          // a read already in flight, its settle or timeout decides — the
          // snapshot is never flipped to expired across an open read.
          if (live() && !inFlight) {
            setSnapshot(EXPIRED_WRITE_AUTHORITY);
          }
        }, WRITE_AUTHORITY_SNAPSHOT_MAX_AGE_MS);
      }
      refreshTimer = setTimeout(() => {
        void read();
      }, refreshIntervalMs);
    };

    const onVisibilityChange = (): void => {
      if (document.visibilityState !== "visible" || inFlight) {
        return;
      }
      clearTimeout(refreshTimer);
      void read();
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    void read();

    return () => {
      generationRef.current += 1;
      controller.abort();
      clearTimeout(refreshTimer);
      clearTimeout(expiryTimer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [dismissLegacyOwnedRejection, refreshIntervalMs, sessionActive]);

  useEffect(() => {
    if (!sessionActive) {
      return;
    }
    return subscribeToDomainLegacyOwnedRejections(reportLegacyOwnedRejection);
  }, [reportLegacyOwnedRejection, sessionActive]);

  const value = useMemo<WriteAuthorityContextValue>(
    () => ({ snapshot, ...rejectionState }),
    [rejectionState, snapshot],
  );

  return <WriteAuthorityContext.Provider value={value}>{children}</WriteAuthorityContext.Provider>;
}

/**
 * Outside a provider the hooks fail closed instead of throwing: a missing
 * provider can never enable a write (every domain reads `legacy`/`initial`),
 * and the shell host has nothing to show. `AppProviders` is the single mount.
 */
const NO_PROVIDER_VALUE: WriteAuthorityContextValue = {
  snapshot: FAIL_CLOSED_WRITE_AUTHORITY,
  rejection: null,
  reportLegacyOwnedRejection: () => undefined,
  dismissLegacyOwnedRejection: () => undefined,
  claimLegacyOwnedRejections: () => () => undefined,
};

function useWriteAuthorityContext(): WriteAuthorityContextValue {
  return useContext(WriteAuthorityContext) ?? NO_PROVIDER_VALUE;
}

/**
 * Per-domain write authority; `authority === "new"` is the only enabling
 * answer. The returned object is stable per evaluated snapshot, so its
 * identity marks "a later read was evaluated" (`useLegacyOwnedRejectionRelease`).
 */
export function useWriteAuthority(domain: WriteAuthorityDomain): DomainWriteAuthority {
  const { snapshot } = useWriteAuthorityContext();
  return useMemo(
    () => ({ authority: snapshot.domains[domain], source: snapshot.source }),
    [domain, snapshot],
  );
}

export function useLegacyOwnedRejection(): Pick<
  WriteAuthorityContextValue,
  "rejection" | "reportLegacyOwnedRejection" | "dismissLegacyOwnedRejection"
> {
  const { rejection, reportLegacyOwnedRejection, dismissLegacyOwnedRejection } =
    useWriteAuthorityContext();
  return { rejection, reportLegacyOwnedRejection, dismissLegacyOwnedRejection };
}

/** U1: claims a domain's 409 rejections for the surface while it is mounted. */
export function useClaimLegacyOwnedRejection(domain: WriteAuthorityDomain): void {
  const { claimLegacyOwnedRejections } = useWriteAuthorityContext();
  useEffect(() => claimLegacyOwnedRejections(domain), [claimLegacyOwnedRejections, domain]);
}
